import { supabase } from "@/integrations/supabase/client";
import { Event } from "@/types/events";
import { format } from "date-fns";
import { HostProfile } from "@/types/database/host";

interface HostDashboardStats {
  totalEvents: number;
  totalParticipants: number;
  totalRevenue: number;
  upcomingEventsList: Event[];
}

export async function getHostsAvailableOnDate(date: string) {
  const { data: availableHosts, error } = await supabase
    .from('host_profiles')
    .select(`
      id,
      full_name,
      email,
      phone,
      created_at,
      updated_at,
      host_rate_card(*),
      event_host_invites!left(status),
      events!events_host_id_fkey (event_date, event_time),
      profiles!host_profiles_id_fkey (verification_status)
    `)
    .eq('profiles.verification_status', 'approved')
    .neq('event_host_invites.status', 'accepted')
    .eq('events.event_date', date);

  if (error) throw error;
  return availableHosts as HostProfile[];
}

export const getHostDashboardStats = async (hostId: string | undefined): Promise<HostDashboardStats> => {
  if (!hostId) throw new Error("Host ID is required");

  const today = format(new Date(), "yyyy-MM-dd");

  // Get all events for this host with necessary details
  const { data: events = [] } = await supabase
    .from("events")
    .select(`
      *,
      event_participants!event_participants_event_id_fkey (id),
      opportunity_catalog!events_opportunity_catalog_id_fkey (
        id, event_cost, format, format_details, event_cost, min_volunteers, max_volunteers, host_included
        opportunity:opportunities (
          id,
          name,
          format,
          type,
          status,
          focus_areas,
          beneficiary_types
        )
      ),
      corporate_profiles!events_corporate_id_fkey (
        id,
        company_name,
        company_email,
        phone,
        isd_code
      ),
      corporate_contacts!events_corporate_contact_id_fkey (
        id,
        name,
        email,
        contact,
        designation
      )
    `)
    .eq("host_id", hostId)
    .gte("event_date", today);

  const eventsWithDetails = events.map(event => ({
    ...event,
    participant_count: event.event_participants[0]?.count || 0,
    revenue: Number(event.opportunity_catalog?.event_cost) || 0
  }));

  return {
    totalEvents: eventsWithDetails.length,
    totalParticipants: eventsWithDetails.reduce((acc, event) => acc + event.participant_count, 0),
    totalRevenue: eventsWithDetails.reduce((acc, event) => acc + event.revenue, 0),
    upcomingEventsList: eventsWithDetails,
  };
};
